import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type AgentJudgeMappingEntry,
  agentObservabilityService,
  type MetricDraft,
} from 'v2/shared/services/agent-observability-service'
import { agentObservabilityQueryKeys } from 'v2/shared/utils/query-key-management'
import type {
  RAgentJudge,
  RAgentJudgesResponse,
  RCalibrationExample,
  RJudge,
  RJudgeDeleteResponse,
  RJudgeScope,
} from 'v2/types/api/agent-observability'
import { errorToast, generateToastsForMutation } from 'v2/utils/toasts'

export interface MetricFormValues {
  display_name: string
  description: string
  scope: RJudgeScope
}

export const useAgentJudgesQuery = (agentId: string) =>
  useQuery({
    queryKey: agentObservabilityQueryKeys().agentJudges(agentId),
    queryFn: () => agentObservabilityService.getAgentJudges(agentId),
    select: (res) => res.objects,
    enabled: !!agentId,
  })

export const SESSIONS_PAGE_SIZE = 10

export const useRecentSessionsQuery = (agentId: string, enabled: boolean, offset = 0) =>
  useQuery({
    queryKey: [...agentObservabilityQueryKeys().recentSessions(agentId), offset],
    queryFn: () => agentObservabilityService.getRecentSessions(agentId, SESSIONS_PAGE_SIZE, offset),
    enabled: enabled && !!agentId,
  })

const useInvalidateAgentJudges = (agentId: string) => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      queryKey: agentObservabilityQueryKeys().agentJudges(agentId),
    })
}

/** The PUT is a full replace, so every mapping write starts from the server's
 *  current set (not the query cache, which another tab may have outrun). */
const fetchCurrentMappings = async (agentId: string): Promise<AgentJudgeMappingEntry[]> => {
  const { objects } = await agentObservabilityService.getAgentJudges(agentId)
  return objects.map((j) => ({ judge_id: j.id, enabled: j.mapping_enabled }))
}

/** POST the judge, then map it to this agent (off) in the same action — an
 *  unmapped judge would exist in the registry but never appear in this list. */
export const useCreateMetricMutation = (agentId: string) => {
  const invalidate = useInvalidateAgentJudges(agentId)
  return useMutation({
    mutationFn: async (values: MetricFormValues) => {
      const judge = await agentObservabilityService.createJudge({ ...values, enabled: false })
      const current = await fetchCurrentMappings(agentId)
      await agentObservabilityService.setAgentJudges(agentId, [
        ...current.filter((entry) => entry.judge_id !== judge.id),
        { judge_id: judge.id, enabled: false },
      ])
      return judge
    },
    ...generateToastsForMutation<RJudge, MetricFormValues>(
      { title: 'Metric created', callback: invalidate },
      { title: 'Failed to create metric' },
    ),
  })
}

export const useUpdateMetricMutation = (agentId: string) => {
  const invalidate = useInvalidateAgentJudges(agentId)
  return useMutation({
    mutationFn: (payload: { id: string; values: MetricFormValues }) =>
      agentObservabilityService.updateJudge(payload.id, payload.values),
    ...generateToastsForMutation<RJudge, { id: string; values: MetricFormValues }>(
      { title: 'Metric updated', callback: invalidate },
      { title: 'Failed to update metric' },
    ),
  })
}

export const useDeleteMetricMutation = (agentId: string) => {
  const invalidate = useInvalidateAgentJudges(agentId)
  return useMutation({
    mutationFn: (id: string) => agentObservabilityService.deleteJudge(id),
    ...generateToastsForMutation<RJudgeDeleteResponse, string>(
      { title: 'Metric deleted', callback: invalidate },
      { title: 'Failed to delete metric' },
    ),
  })
}

/** Live requires BOTH switches (registry `enabled` AND mapping enabled), and
 *  POST creates registry-off — so turning on flips the registry switch too.
 *  Turning off pauses only this agent's mapping: the judge may be live on
 *  other agents. */
export const useToggleMetricMutation = (agentId: string) => {
  const invalidate = useInvalidateAgentJudges(agentId)
  return useMutation({
    mutationFn: async (payload: { judge: RAgentJudge; enabled: boolean }) => {
      const { judge, enabled } = payload
      if (enabled && !judge.enabled) {
        await agentObservabilityService.updateJudge(judge.id, { enabled: true })
      }
      const current = await fetchCurrentMappings(agentId)
      return agentObservabilityService.setAgentJudges(agentId, [
        ...current.filter((entry) => entry.judge_id !== judge.id),
        { judge_id: judge.id, enabled },
      ])
    },
    ...generateToastsForMutation<RAgentJudgesResponse, { judge: RAgentJudge; enabled: boolean }>(
      { title: 'Metric status updated', callback: invalidate },
      { title: 'Failed to update metric status' },
    ),
  })
}

/** Dry run — verdicts render in the sheet, so success needs no toast. */
export const useTestMetricMutation = () =>
  useMutation({
    mutationFn: (payload: { judgeId: string; sessionIds: string[] }) =>
      agentObservabilityService.testJudge(payload.judgeId, payload.sessionIds),
    onError: () => errorToast({ title: 'Failed to test metric' }),
  })

/** Suggest metrics from the flow (AO summarizes the flow server-side). Returns
 *  candidate drafts the user reviews and Saves; nothing is persisted here. */
export const useGenerateMetricsMutation = () =>
  useMutation({
    mutationFn: async (payload: { flow: unknown; existing: MetricDraft[] }) => {
      const res = await agentObservabilityService.generateMetrics(payload.flow, payload.existing)
      return res.metrics ?? []
    },
    onError: () => errorToast({ title: 'Could not generate metrics' }),
  })

/** Rewrite the typed name+description into a crisp pass/fail rubric. */
export const useImproveDescriptionMutation = () =>
  useMutation({
    mutationFn: (draft: MetricDraft) => agentObservabilityService.improveDescription(draft),
    onError: () => errorToast({ title: 'Could not improve the description' }),
  })

/** Refine the rubric from a flagged verdict — the human-correct verdict for a
 *  call becomes a labelled example. Returns an improved description to review. */
export const useCalibrateMetricMutation = () =>
  useMutation({
    mutationFn: (payload: {
      description: string
      scope: RJudgeScope
      examples: RCalibrationExample[]
    }) => agentObservabilityService.calibrateMetric(payload),
    onError: () => errorToast({ title: 'Could not refine the metric' }),
  })
